/**
 * EOS L10 Meeting Room - Enhanced Guided Flow
 * 
 * Proper EOS L10 Structure:
 * 1. Segue (5 min) - Each member shares good news, one at a time
 * 2. Scorecard (5 min) - Review company metrics together
 * 3. Rock Review (5 min) - Each member reviews their rocks
 * 4. Headlines (5 min) - Customer/Employee news, each member
 * 5. To-Do List (5 min) - Each member's to-dos
 * 6. IDS (60 min) - Identify, Discuss, Solve - team discussion
 * 7. Conclude (5 min) - Recap, cascade, rate
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import UserAvatar from '../components/UserAvatar';
import AIAssistedSegue from '../components/AIAssistedSegue';
import MeetingConclude from '../components/MeetingConclude';
import { 
  Users, Clock, PlayCircle, PauseCircle, CheckCircle2, 
  MessageSquare, ListChecks, Target, TrendingUp, 
  ArrowLeft, Zap, Minimize2, Maximize2, Loader2, Crown,
  ChevronRight, ChevronLeft, Hand, UserCheck, UserPlus, UserMinus,
  Video, VideoOff, Mic, MicOff, Phone, Camera,
  Sparkles, PanelRightClose, PanelRight, Settings2, Building,
  User, SkipForward, Home, AlertTriangle, Eye, Megaphone
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// EOS L10 Standard Sections with proper flow
// perMember: true = cycles through each attendee
// companyFirst: true = shows company-level first, then individual users
const L10_SECTIONS = [
  { key: 'segue', label: 'Segue', icon: Users, duration: 5, color: 'bg-blue-500', description: 'Personal & Professional Good News', perMember: true },
  { key: 'scorecard', label: 'Scorecard', icon: TrendingUp, duration: 10, color: 'bg-green-500', description: 'Company & Individual Metrics', perMember: true, companyFirst: true },
  { key: 'rocks', label: 'Rock Review', icon: Target, duration: 5, color: 'bg-purple-500', description: 'Quarterly goals status', perMember: true },
  { key: 'headlines', label: 'Headlines', icon: Megaphone, duration: 5, color: 'bg-yellow-500', description: 'Customer/Employee news', perMember: true },
  { key: 'todos', label: 'To-Do List', icon: ListChecks, duration: 5, color: 'bg-cyan-500', description: 'Review last week commitments', perMember: true },
  { key: 'ids', label: 'IDS', icon: Zap, duration: 60, color: 'bg-red-500', description: 'Identify, Discuss, Solve', perMember: true },
  { key: 'conclude', label: 'Conclude', icon: CheckCircle2, duration: 5, color: 'bg-emerald-500', description: 'Recap & rate meeting', perMember: false },
];

const EOSL10MeetingRoomEnhanced = () => {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  
  // Core state
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [isFacilitator, setIsFacilitator] = useState(false);
  
  // Navigation state
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentMemberIndex, setCurrentMemberIndex] = useState(0);
  
  // Attendees state
  const [attendees, setAttendees] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [showAttendeesDialog, setShowAttendeesDialog] = useState(false);
  
  // Timer state
  const [overallTimer, setOverallTimer] = useState(0);
  const [sectionTimer, setSectionTimer] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  
  // EOS Data state
  const [sectionData, setSectionData] = useState({
    scorecard: { metrics: [], summary: {}, period: {} },
    rocks: [],
    headlines: [],
    todos: [],
    issues: [],
  });
  
  // UI state
  const [copilotExpanded, setCopilotExpanded] = useState(false);
  const [videoExpanded, setVideoExpanded] = useState(false);
  const [videoJoined, setVideoJoined] = useState(false);
  const [localVideo, setLocalVideo] = useState(true);
  const [localAudio, setLocalAudio] = useState(true);
  const [collapsedCategories, setCollapsedCategories] = useState({});
  
  // Inline update state (for IDS and Todo actions)
  const [updatingIssueId, setUpdatingIssueId] = useState(null);
  const [updatingTodoId, setUpdatingTodoId] = useState(null);
  
  // Toggle category collapse
  const toggleCategory = (cat) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [cat]: !prev[cat]
    }));
  };
  
  // Refs
  const timerRef = useRef(null);
  const pollRef = useRef(null);

  // Current section info
  const currentSection = L10_SECTIONS[currentSectionIndex];
  const presentAttendees = attendees.filter(a => a.status === 'present');
  
  // For companyFirst sections, -1 = company level, 0+ = individual members
  // For regular perMember sections, 0+ = individual members
  const isCompanyLevel = currentSection?.companyFirst && currentMemberIndex === -1;
  
  // Safely get current member, clamping index to valid range
  // Use currentMemberIndex directly since it's managed by navigation handlers
  const effectiveMemberIndex = presentAttendees.length > 0 
    ? Math.min(Math.max(0, currentMemberIndex), presentAttendees.length - 1)
    : 0;
  const currentMember = currentSection?.perMember && !isCompanyLevel && presentAttendees.length > 0
    ? presentAttendees[effectiveMemberIndex] 
    : null;

  // Initialize
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) setCurrentUser(JSON.parse(userStr));
  }, []);

  useEffect(() => {
    fetchMeeting();
    fetchEOSData();
    fetchAvailableUsers();
    
    pollRef.current = setInterval(fetchMeetingState, 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [meetingId]);

  // Timer effect
  useEffect(() => {
    if (isTimerRunning && !isPaused) {
      timerRef.current = setInterval(() => {
        setOverallTimer(prev => prev + 1);
        setSectionTimer(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isTimerRunning, isPaused]);

  // Enrich attendees with display_name, department, and role from availableUsers
  useEffect(() => {
    if (attendees.length > 0 && availableUsers.length > 0) {
      const enrichedAttendees = attendees.map(attendee => {
        const user = availableUsers.find(u => 
          (u.id || u.user_id) === attendee.user_id
        );
        
        if (user) {
          // Always update display_name from user data if missing
          const displayName = attendee.display_name || 
            `${user.first_name || ''} ${user.last_name || ''}`.trim() || 
            user.email || 
            'Unknown User';
          
          return {
            ...attendee,
            display_name: displayName,
            department: attendee.department || user.department || '',
            role: attendee.role || user.role || '',
            first_name: attendee.first_name || user.first_name || '',
            last_name: attendee.last_name || user.last_name || ''
          };
        }
        
        // Fallback if user not found in availableUsers
        const fallbackName = attendee.first_name 
          ? `${attendee.first_name || ''} ${attendee.last_name || ''}`.trim() 
          : null;
        
        return {
          ...attendee,
          display_name: attendee.display_name || fallbackName || 'Unknown User'
        };
      });
      
      // Only update if there are actual changes
      const hasChanges = enrichedAttendees.some((a, i) => 
        a.display_name !== attendees[i].display_name ||
        a.department !== attendees[i].department || 
        a.role !== attendees[i].role
      );
      
      if (hasChanges) {
        setAttendees(enrichedAttendees);
      }
    }
  }, [attendees, availableUsers]);

  const fetchMeeting = async () => {
    try {
      const response = await axios.get(`${API}/eos/meetings/${meetingId}`);
      setMeeting(response.data);
      
      const meetingAttendees = response.data.attendees || [];
      // Ensure each attendee has a display_name
      setAttendees(meetingAttendees.map(a => {
        // Build display_name from first/last name if available
        const nameFromParts = a.first_name 
          ? `${a.first_name} ${a.last_name || ''}`.trim() 
          : null;
        
        return {
          ...a,
          status: a.status || 'present',
          // Set display_name from various possible sources
          display_name: a.display_name || nameFromParts || a.name || a.email || 'Unknown User'
        };
      }));
      
      const userId = JSON.parse(localStorage.getItem('user'))?.user_id;
      setIsFacilitator(response.data.facilitator_id === userId || response.data.created_by === userId);
      
      // Restore state
      if (response.data.current_section_index !== undefined) {
        setCurrentSectionIndex(response.data.current_section_index);
      }
      if (response.data.current_member_index !== undefined) {
        setCurrentMemberIndex(response.data.current_member_index);
      }
    } catch (error) {
      console.error('Error fetching meeting:', error);
      toast.error('Failed to load meeting');
    } finally {
      setLoading(false);
    }
  };

  const fetchMeetingState = async () => {
    try {
      const response = await axios.get(`${API}/eos/meetings/${meetingId}/state`);
      if (response.data) {
        if (response.data.current_section_index !== undefined) {
          setCurrentSectionIndex(response.data.current_section_index);
        }
        if (response.data.current_member_index !== undefined) {
          setCurrentMemberIndex(response.data.current_member_index);
        }
      }
    } catch (error) {
      // Silent fail for polling
    }
  };

  const fetchEOSData = async () => {
    try {
      const [scorecard, rocks, headlines, todos, issues] = await Promise.all([
        axios.get(`${API}/eos/live-scorecard`),
        axios.get(`${API}/eos/rocks`),
        axios.get(`${API}/eos/headlines`),
        axios.get(`${API}/eos/todos`),
        axios.get(`${API}/eos/issues`)
      ]);
      
      setSectionData({
        scorecard: {
          metrics: scorecard.data.metrics,
          summary: scorecard.data.summary,
          period: scorecard.data.period,
        },
        rocks: rocks.data,
        headlines: headlines.data,
        todos: todos.data,
        issues: issues.data
      });
    } catch (error) {
      console.error('Error fetching EOS data:', error);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setAvailableUsers(response.data.filter(u => u.is_active !== false));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const updateMeetingState = async (updates) => {
    try {
      await axios.put(`${API}/eos/meetings/${meetingId}/state`, updates);
    } catch (error) {
      console.error('Error updating meeting state:', error);
    }
  };

  // ============================================================================
  // FIX 1: IDS Inline Actions - Update issue status
  // ============================================================================
  const handleUpdateIssueStatus = async (issueId, newStatus) => {
    setUpdatingIssueId(issueId);
    try {
      await axios.put(`${API}/eos/issues/${issueId}`, { status: newStatus });
      toast.success(newStatus === 'solved' ? 'Issue solved! ✓' : 'Issue marked as discussing');
      // Refresh issues data
      const issues = await axios.get(`${API}/eos/issues`);
      setSectionData(prev => ({ ...prev, issues: issues.data }));
    } catch (error) {
      console.error('Error updating issue status:', error);
      toast.error('Failed to update issue');
    } finally {
      setUpdatingIssueId(null);
    }
  };

  // ============================================================================
  // FIX 2: To-Do Complete Toggle - Toggle todo status
  // ============================================================================
  const handleToggleTodoStatus = async (todoId, currentStatus) => {
    setUpdatingTodoId(todoId);
    try {
      if (currentStatus === 'complete') {
        // Reopen the todo
        await axios.patch(`${API}/eos/todos/${todoId}/reopen`);
        toast.success('To-do reopened');
      } else {
        // Complete the todo
        await axios.patch(`${API}/eos/todos/${todoId}/complete`);
        toast.success('To-do completed! ✓');
      }
      // Refresh todos data
      const todos = await axios.get(`${API}/eos/todos`);
      setSectionData(prev => ({ ...prev, todos: todos.data }));
    } catch (error) {
      console.error('Error toggling todo status:', error);
      toast.error('Failed to update to-do');
    } finally {
      setUpdatingTodoId(null);
    }
  };

  // Attendee management
  const handleAddAttendee = (user) => {
    const newAttendee = {
      user_id: user.id || user.user_id,
      display_name: `${user.first_name} ${user.last_name}`,
      email: user.email,
      status: 'present',
      department: user.department || '',
      role: user.role || ''
    };
    
    if (!attendees.find(a => a.user_id === newAttendee.user_id)) {
      const updated = [...attendees, newAttendee];
      setAttendees(updated);
      saveMeetingAttendees(updated);
      toast.success(`${newAttendee.display_name} added to meeting`);
    }
  };

  const handleRemoveAttendee = (userId) => {
    const updated = attendees.filter(a => a.user_id !== userId);
    setAttendees(updated);
    saveMeetingAttendees(updated);
    toast.success('Attendee removed');
  };

  const handleToggleAttendeeStatus = (userId) => {
    const updated = attendees.map(a => 
      a.user_id === userId 
        ? { ...a, status: a.status === 'present' ? 'absent' : 'present' }
        : a
    );
    setAttendees(updated);
    saveMeetingAttendees(updated);
  };

  const saveMeetingAttendees = async (newAttendees) => {
    try {
      await axios.put(`${API}/eos/meetings/${meetingId}`, { attendees: newAttendees });
    } catch (error) {
      console.error('Error saving attendees:', error);
    }
  };

  // Navigation - Next Member (within same section)
  const handleNextMember = () => {
    if (!currentSection?.perMember) return;
    
    // For companyFirst sections, -1 is company level
    const effectiveIndex = currentSection?.companyFirst ? currentMemberIndex : currentMemberIndex;
    const maxIndex = presentAttendees.length - 1;
    
    if (currentSection?.companyFirst && currentMemberIndex === -1) {
      // Move from company level to first member
      setCurrentMemberIndex(0);
      setSectionTimer(0);
      updateMeetingState({ current_member_index: 0 });
      if (presentAttendees[0]) {
        toast.info(`Now: ${presentAttendees[0]?.display_name}'s metrics`);
      }
    } else if (currentMemberIndex < maxIndex) {
      // Move to next member
      const nextIdx = currentMemberIndex + 1;
      setCurrentMemberIndex(nextIdx);
      setSectionTimer(0);
      updateMeetingState({ current_member_index: nextIdx });
      toast.info(`Now: ${presentAttendees[nextIdx]?.display_name}`);
    } else {
      // All members done, move to next section
      handleNextSection();
    }
  };

  // Navigation - Next Section
  const handleNextSection = () => {
    setSectionTimer(0);
    
    if (currentSectionIndex < L10_SECTIONS.length - 1) {
      const nextIdx = currentSectionIndex + 1;
      const nextSection = L10_SECTIONS[nextIdx];
      
      // If next section has companyFirst, start at -1 (company level)
      const startMemberIdx = nextSection.companyFirst ? -1 : 0;
      
      setCurrentSectionIndex(nextIdx);
      setCurrentMemberIndex(startMemberIdx);
      updateMeetingState({ current_section_index: nextIdx, current_member_index: startMemberIdx });
      toast.info(`Moving to: ${nextSection.label}`);
    } else {
      // Meeting complete
      handleCompleteMeeting();
    }
  };

  // Navigation - Previous
  const handlePrevSection = () => {
    setSectionTimer(0);
    
    // Handle going back within perMember sections
    if (currentSection?.perMember) {
      if (currentSection?.companyFirst && currentMemberIndex === 0) {
        // Go back to company level
        setCurrentMemberIndex(-1);
        updateMeetingState({ current_member_index: -1 });
        return;
      } else if (currentMemberIndex > 0) {
        // Go to previous member
        const prevIdx = currentMemberIndex - 1;
        setCurrentMemberIndex(prevIdx);
        updateMeetingState({ current_member_index: prevIdx });
        return;
      } else if (currentSection?.companyFirst && currentMemberIndex === -1) {
        // Already at company level, go to previous section
      } else if (currentMemberIndex === 0) {
        // At first member, go to previous section
      }
    }
    
    if (currentSectionIndex > 0) {
      // Go to previous section
      const prevIdx = currentSectionIndex - 1;
      const prevSection = L10_SECTIONS[prevIdx];
      setCurrentSectionIndex(prevIdx);
      
      // Set to last member of previous section
      const newMemberIdx = prevSection.perMember ? presentAttendees.length - 1 : 0;
      setCurrentMemberIndex(newMemberIdx);
      updateMeetingState({ current_section_index: prevIdx, current_member_index: newMemberIdx });
    }
  };

  // Complete meeting
  const handleCompleteMeeting = async () => {
    try {
      await axios.post(`${API}/eos/meetings/${meetingId}/complete`);
      toast.success('Meeting completed! 🎉');
      setIsTimerRunning(false);
      setTimeout(() => navigate('/eos/l10-meetings'), 1500);
    } catch (error) {
      console.error('Error completing meeting:', error);
      toast.error('Failed to complete meeting');
    }
  };

  // Timer controls
  const handleStartMeeting = () => {
    setIsTimerRunning(true);
    setIsPaused(false);
    toast.success('Meeting started!');
  };

  const handlePauseMeeting = () => {
    setIsPaused(!isPaused);
  };

  // Video controls
  const handleToggleVideo = () => {
    if (!videoJoined) {
      setVideoJoined(true);
      setVideoExpanded(true);
      toast.success('Joined video huddle');
    } else {
      setVideoJoined(false);
      setVideoExpanded(false);
      toast.info('Left video huddle');
    }
  };

  // Helpers
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressPercent = () => {
    const totalSections = L10_SECTIONS.length;
    let progress = (currentSectionIndex / totalSections) * 100;
    
    // Add partial progress for per-member sections
    if (currentSection?.perMember && presentAttendees.length > 0) {
      const memberProgress = (currentMemberIndex / presentAttendees.length) * (100 / totalSections);
      progress += memberProgress;
    }
    
    return Math.min(Math.round(progress), 100);
  };

  // Get member-specific data
  const getMemberData = () => {
    if (!currentMember) return sectionData;
    
    const memberId = currentMember.user_id;
    return {
      ...sectionData,
      rocks: sectionData.rocks.filter(r => r.owner_id === memberId),
      todos: sectionData.todos.filter(t => t.owner_id === memberId),
      headlines: sectionData.headlines.filter(h => h.author_id === memberId),
    };
  };

  // Check if section timer exceeded
  const isOverTime = sectionTimer > (currentSection?.duration || 5) * 60;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-900">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="mt-4 text-slate-400">Loading meeting...</p>
        </div>
      </div>
    );
  }

  const memberData = getMemberData();
  const isInPerson = meeting?.is_in_person;

  return (
    <div className="h-screen flex flex-col bg-slate-950 text-white" data-testid="l10-meeting-room">
      {/* Compact Header */}
      <header className="bg-slate-900 border-b border-slate-800 px-4 py-2">
        <div className="flex items-center justify-between">
          {/* Left: Back + Meeting Title */}
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/eos/l10-meetings')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-sm font-semibold">{meeting?.title || 'L10 Meeting'}</h1>
                {isInPerson && (
                  <Badge variant="outline" className="text-xs bg-blue-500/20 border-blue-500 text-blue-400">
                    <Building className="h-3 w-3 mr-1" />
                    In-Person
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Badge variant="outline" className="text-xs">
                  {currentSection?.label || 'Starting'}
                </Badge>
                {currentSection?.perMember && (
                  <>
                    <span>•</span>
                    {isCompanyLevel ? (
                      <span className="text-green-400 flex items-center gap-1">
                        <Building className="h-3 w-3" />
                        Company Level
                      </span>
                    ) : currentMember ? (
                      <span className="text-cyan-400">{currentMember.display_name}</span>
                    ) : null}
                  </>
                )}
                <span>•</span>
                <span>{presentAttendees.length} attendees</span>
              </div>
            </div>
          </div>
          
          {/* Center: Progress */}
          <div className="flex items-center gap-4">
            <div className="w-48">
              <Progress value={getProgressPercent()} className="h-2" />
              <p className="text-[10px] text-slate-500 text-center mt-1">{getProgressPercent()}% complete</p>
            </div>
          </div>
          
          {/* Right: Controls */}
          <div className="flex items-center gap-2">
            {/* Section Timer */}
            <div className={`flex items-center gap-2 px-3 py-1 rounded-lg ${isOverTime ? 'bg-red-900/50 border border-red-500' : 'bg-slate-800'}`}>
              <Clock className={`h-4 w-4 ${isOverTime ? 'text-red-400' : 'text-slate-400'}`} />
              <span className={`font-mono text-sm ${isOverTime ? 'text-red-400' : ''}`}>
                {formatTime(sectionTimer)} / {currentSection?.duration}:00
              </span>
            </div>
            
            {/* Overall Timer */}
            <div className="flex items-center gap-2 px-3 py-1 bg-slate-800 rounded-lg">
              <span className="font-mono text-lg">{formatTime(overallTimer)}</span>
              {!isTimerRunning ? (
                <Button size="sm" variant="ghost" onClick={handleStartMeeting} className="h-7 w-7 p-0">
                  <PlayCircle className="h-5 w-5 text-green-500" />
                </Button>
              ) : (
                <Button size="sm" variant="ghost" onClick={handlePauseMeeting} className="h-7 w-7 p-0">
                  {isPaused ? <PlayCircle className="h-5 w-5 text-green-500" /> : <PauseCircle className="h-5 w-5 text-yellow-500" />}
                </Button>
              )}
            </div>
            
            {/* Video Toggle */}
            <Button
              variant={videoJoined ? 'default' : 'outline'}
              size="sm"
              onClick={handleToggleVideo}
              className={videoJoined ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              {videoJoined ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
            </Button>
            
            {/* Copilot Toggle */}
            <Button
              variant={copilotExpanded ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCopilotExpanded(!copilotExpanded)}
            >
              <Sparkles className="h-4 w-4" />
            </Button>
            
            {/* Attendees */}
            <Button variant="outline" size="sm" onClick={() => setShowAttendeesDialog(true)}>
              <Users className="h-4 w-4 mr-1" />
              {presentAttendees.length}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Agenda */}
        <aside className="w-60 bg-slate-900/50 border-r border-slate-800 p-3 overflow-y-auto">
          <h3 className="text-xs font-semibold text-slate-400 mb-3">L10 AGENDA</h3>
          
          {L10_SECTIONS.map((section, idx) => {
            const isCurrent = currentSectionIndex === idx;
            const isPast = currentSectionIndex > idx;
            const SectionIcon = section.icon;
            
            return (
              <button
                key={section.key}
                onClick={() => {
                  setCurrentSectionIndex(idx);
                  // For companyFirst sections, start at company level (-1)
                  const startIdx = section.companyFirst ? -1 : 0;
                  setCurrentMemberIndex(startIdx);
                  setSectionTimer(0);
                  updateMeetingState({ current_section_index: idx, current_member_index: startIdx });
                }}
                className={`w-full text-left p-2 rounded-lg mb-1 transition-all text-xs flex items-center gap-2 ${
                  isCurrent ? 'bg-primary/20 border border-primary text-white' : 
                  isPast ? 'bg-slate-800/50 text-slate-500' : 
                  'hover:bg-slate-800/50 text-slate-400'
                }`}
              >
                <div className={`p-1.5 rounded ${section.color}`}>
                  <SectionIcon className="h-3 w-3 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-1">
                    <span className="font-medium">{section.label}</span>
                    {section.perMember && <User className="h-2.5 w-2.5 text-cyan-400" />}
                    {section.companyFirst && <Building className="h-2.5 w-2.5 text-green-400" />}
                  </div>
                  <div className="text-[10px] text-slate-500">{section.duration} min</div>
                </div>
                {isPast && <CheckCircle2 className="h-3 w-3 text-green-500" />}
                {isCurrent && <Hand className="h-3 w-3 text-primary animate-pulse" />}
              </button>
            );
          })}
          
          {/* Meeting Attendees - Always show when there are attendees */}
          {presentAttendees.length > 0 && (
            <div className="mt-4 p-2 bg-slate-800/50 rounded-lg">
              <h4 className="text-[10px] font-semibold text-cyan-400 mb-2">
                {currentSection?.perMember 
                  ? currentSection?.companyFirst
                    ? `PROGRESS (${isCompanyLevel ? 'Company' : `${currentMemberIndex + 1}/${presentAttendees.length}`})`
                    : `MEMBER PROGRESS (${currentMemberIndex + 1}/${presentAttendees.length})`
                  : `ATTENDEES (${presentAttendees.length})`}
              </h4>
              
              {/* Company Level indicator for companyFirst sections */}
              {currentSection?.companyFirst && (
                <div 
                  className={`flex items-center gap-2 p-1.5 rounded text-[10px] mb-1 ${
                    isCompanyLevel ? 'bg-green-500/20 text-green-300 border border-green-500/30' : 'text-slate-500'
                  }`}
                >
                  <Building className="h-3 w-3" />
                  <span className="flex-1">Company</span>
                  {isCompanyLevel && <Hand className="h-2.5 w-2.5 text-green-400" />}
                  {!isCompanyLevel && <CheckCircle2 className="h-2.5 w-2.5 text-green-500" />}
                </div>
              )}
              
              <div className="space-y-1">
                {presentAttendees.map((attendee, idx) => {
                  const isCurrent = currentSection?.perMember && !isCompanyLevel && currentMemberIndex === idx;
                  const isDone = currentSection?.perMember && (isCompanyLevel ? false : currentMemberIndex > idx);
                  const deptLabel = getDepartmentLabel(attendee.department, attendee.role);
                  
                  return (
                    <div 
                      key={attendee.user_id}
                      className={`flex items-center gap-2 p-1.5 rounded text-[10px] ${
                        isCurrent ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30' : 
                        isDone ? 'text-slate-500' : 'text-slate-400'
                      }`}
                    >
                      <UserAvatar user={attendee} size="xs" />
                      <div className="flex-1 min-w-0">
                        <span className="block truncate">{attendee.display_name?.split(' ')[0]}</span>
                        <span className={`text-[9px] ${isCurrent ? 'text-cyan-400' : 'text-slate-500'}`}>
                          {deptLabel}
                        </span>
                      </div>
                      {isDone && <CheckCircle2 className="h-2.5 w-2.5 text-green-500" />}
                      {isCurrent && <Hand className="h-2.5 w-2.5 text-cyan-400" />}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main className={`flex-1 p-4 overflow-y-auto ${copilotExpanded ? 'mr-72' : ''}`}>
          {/* Section Header */}
          <Card className="mb-4 bg-slate-900/50 border-slate-800">
            <CardContent className="py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${currentSection?.color}`}>
                    {currentSection && <currentSection.icon className="h-5 w-5 text-white" />}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold">{currentSection?.label}</h2>
                    <p className="text-xs text-slate-400">{currentSection?.description}</p>
                  </div>
                </div>
                
                {/* Current Member/Company Badge */}
                {currentSection?.perMember && (
                  isCompanyLevel ? (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/20 rounded-lg border border-green-500/50">
                      <Building className="h-5 w-5 text-green-400" />
                      <div>
                        <p className="text-sm font-medium">Company Level</p>
                        <p className="text-xs text-green-400">All team metrics</p>
                      </div>
                    </div>
                  ) : currentMember ? (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-cyan-500/20 rounded-lg border border-cyan-500/50">
                      <UserAvatar user={currentMember} size="sm" />
                      <div>
                        <p className="text-sm font-medium">{currentMember.display_name}</p>
                        <p className="text-xs text-cyan-400">{currentMemberIndex + 1} of {presentAttendees.length}</p>
                      </div>
                    </div>
                  ) : null
                )}
              </div>
            </CardContent>
          </Card>

          {/* Section Content */}
          <Card className="bg-slate-900/30 border-slate-800 min-h-[350px]">
            <CardContent className="py-4">
              {renderSectionContent(currentSection?.key, memberData, currentMember, meetingId, presentAttendees, isCompanyLevel, collapsedCategories, toggleCategory, {
                handleToggleTodoStatus,
                handleUpdateIssueStatus,
                updatingTodoId,
                updatingIssueId
              })}
            </CardContent>
          </Card>

          {/* Navigation Controls */}
          <div className="flex items-center justify-between mt-4">
            <Button
              variant="outline"
              onClick={handlePrevSection}
              disabled={currentSectionIndex === 0 && (currentSection?.companyFirst ? currentMemberIndex === -1 : currentMemberIndex === 0)}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            
            <div className="flex items-center gap-2">
              {/* Next Member Button (for per-member sections including companyFirst) */}
              {currentSection?.perMember && (presentAttendees.length > 0 || currentSection?.companyFirst) && (
                <Button 
                  variant="outline" 
                  onClick={handleNextMember}
                  className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/20"
                >
                  <SkipForward className="h-4 w-4 mr-1" />
                  {isCompanyLevel 
                    ? 'Start Individual Review' 
                    : currentMemberIndex < presentAttendees.length - 1 
                      ? 'Next Member' 
                      : 'Finish Section'}
                </Button>
              )}
              
              {/* Next Section / Complete Meeting Button */}
              {currentSectionIndex === L10_SECTIONS.length - 1 ? (
                <Button onClick={handleCompleteMeeting} className="bg-green-600 hover:bg-green-700">
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Complete Meeting
                </Button>
              ) : (
                <Button onClick={handleNextSection} className="bg-cyan-600 hover:bg-cyan-700">
                  Next Section
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </main>

        {/* Right Sidebar - Copilot (Collapsible) */}
        {copilotExpanded && (
          <aside className="absolute right-0 top-[57px] bottom-0 w-72 bg-slate-900 border-l border-slate-800 p-3 overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-cyan-500" />
                AI Copilot
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setCopilotExpanded(false)}>
                <PanelRightClose className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="space-y-3">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="py-3">
                  <p className="text-xs text-slate-400 mb-2">Copilot can help with:</p>
                  <ul className="text-xs space-y-1 text-slate-300">
                    <li>• Meeting notes & summaries</li>
                    <li>• Action item extraction</li>
                    <li>• Issue identification</li>
                    <li>• Time management alerts</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </aside>
        )}

        {/* Video Huddle (Floating) */}
        {videoJoined && videoExpanded && (
          <div className="absolute bottom-4 right-4 w-80 bg-slate-900 rounded-lg border border-slate-700 shadow-xl">
            <div className="flex items-center justify-between p-2 border-b border-slate-700">
              <span className="text-xs font-medium">Video Huddle</span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setVideoExpanded(false)}>
                <Minimize2 className="h-3 w-3" />
              </Button>
            </div>
            <div className="aspect-video bg-slate-800 flex items-center justify-center">
              <div className="text-center text-slate-500">
                <Camera className="h-8 w-8 mx-auto mb-2" />
                <p className="text-xs">Video Preview</p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-2 p-2 border-t border-slate-700">
              <Button
                variant={localAudio ? 'default' : 'destructive'}
                size="sm"
                onClick={() => setLocalAudio(!localAudio)}
                className="h-8 w-8 p-0"
              >
                {localAudio ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
              </Button>
              <Button
                variant={localVideo ? 'default' : 'destructive'}
                size="sm"
                onClick={() => setLocalVideo(!localVideo)}
                className="h-8 w-8 p-0"
              >
                {localVideo ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleToggleVideo}
                className="h-8 w-8 p-0"
              >
                <Phone className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
        
        {/* Minimized Video */}
        {videoJoined && !videoExpanded && (
          <Button
            className="absolute bottom-4 right-4 bg-green-600 hover:bg-green-700"
            onClick={() => setVideoExpanded(true)}
          >
            <Video className="h-4 w-4 mr-2" />
            Video Active
          </Button>
        )}
      </div>

      {/* Attendees Dialog */}
      <Dialog open={showAttendeesDialog} onOpenChange={setShowAttendeesDialog}>
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Meeting Attendees
            </DialogTitle>
            <DialogDescription>
              Manage who is attending this L10 meeting
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Current Attendees */}
            <div>
              <Label className="text-sm mb-2 block">Current Attendees ({attendees.length})</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {attendees.map(attendee => (
                  <div key={attendee.user_id} className="flex items-center justify-between p-2 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <UserAvatar user={attendee} size="sm" />
                      <span className="text-sm">{attendee.display_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant={attendee.status === 'present' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handleToggleAttendeeStatus(attendee.user_id)}
                        className="text-xs"
                      >
                        {attendee.status === 'present' ? (
                          <><Eye className="h-3 w-3 mr-1" /> Present</>
                        ) : (
                          <><UserMinus className="h-3 w-3 mr-1" /> Absent</>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveAttendee(attendee.user_id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <UserMinus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {attendees.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">No attendees added yet</p>
                )}
              </div>
            </div>
            
            {/* Add Attendee */}
            <div>
              <Label className="text-sm mb-2 block">Add Attendee</Label>
              <Select onValueChange={(userId) => {
                const user = availableUsers.find(u => (u.id || u.user_id) === userId);
                if (user) handleAddAttendee(user);
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800">
                  {availableUsers
                    .filter(u => !attendees.find(a => a.user_id === (u.id || u.user_id)))
                    .map(user => (
                      <SelectItem key={user.id || user.user_id} value={user.id || user.user_id}>
                        {user.first_name} {user.last_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowAttendeesDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Section Content Renderer
// Helper function to get readable department label
const getDepartmentLabel = (department, role) => {
  const dept = (department || '').toLowerCase().trim();
  const r = (role || '').toLowerCase().trim();
  
  // Priority to department, then role
  if (dept === 'hq') return 'HQ / Leadership';
  if (dept === 'sales') return 'Sales';
  if (dept === 'operations') return 'Operations';
  if (dept === 'finance' || dept === 'financial') return 'Finance';
  if (dept === 'marketing') return 'Marketing';
  if (dept === 'call_center' || dept === 'call center') return 'Call Center';
  if (dept === 'platform' || dept === 'engineering') return 'Platform';
  
  // Fall back to role
  if (r === 'super_admin') return 'Platform Admin';
  if (r === 'admin') return 'Admin';
  if (r === 'ceo') return 'CEO';
  if (r === 'coo') return 'COO';
  if (r === 'cfo') return 'CFO';
  if (r === 'president') return 'President';
  if (r === 'director_of_sales') return 'Dir. of Sales';
  if (r === 'sales_manager') return 'Sales Mgr';
  if (r === 'sales_rep') return 'Sales Rep';
  if (r === 'operations_manager' || r === 'ops_manager') return 'Ops Manager';
  if (r === 'general_manager') return 'Gen. Manager';
  
  return dept || r || 'Team Member';
};

// Department to metric category mapping
// Backend categories: sales, operations, financial, platform
const DEPARTMENT_CATEGORY_MAP = {
  // Leadership sees all categories
  'hq': ['sales', 'operations', 'financial'],
  'leadership': ['sales', 'operations', 'financial'],
  'executive': ['sales', 'operations', 'financial'],
  // Department-specific
  'sales': ['sales'],
  'operations': ['operations'],
  'finance': ['financial'],
  'financial': ['financial'],
  'marketing': ['marketing', 'sales'], // Marketing focuses on marketing and sales metrics
  'call_center': ['sales'], // Call center sees sales metrics
  'call center': ['sales'],
  'platform': ['platform'], // Platform team sees platform metrics
  'engineering': ['platform'],
  // Role-based mappings
  'ceo': ['marketing', 'sales', 'operations', 'financial'],
  'president': ['marketing', 'sales', 'operations', 'financial'],
  'president_&_ceo': ['marketing', 'sales', 'operations', 'financial'],
  'coo': ['operations', 'financial'],
  'cfo': ['financial'],
  'vp_sales': ['marketing', 'sales', 'financial'],
  'director_of_sales': ['marketing', 'sales', 'financial'],
  'sales_rep': ['sales'],
  'sales_manager': ['marketing', 'sales', 'financial'],
  'ops_manager': ['operations'],
  'operations_manager': ['operations'],
  'marketing_manager': ['marketing', 'sales'],
  // Super Admin sees ALL including platform metrics
  'super_admin': ['marketing', 'sales', 'operations', 'financial', 'platform'],
  'admin': ['marketing', 'sales', 'operations', 'financial'],
  'general_manager': ['marketing', 'sales', 'operations', 'financial'],
};

const renderSectionContent = (sectionKey, data, currentMember, meetingId, attendees, isCompanyLevel = false, collapsedCategories = {}, toggleCategory = () => {}, handlers = {}) => {
  // Destructure handlers with defaults
  const { 
    handleToggleTodoStatus = () => {}, 
    handleUpdateIssueStatus = () => {},
    updatingTodoId = null,
    updatingIssueId = null
  } = handlers;
  switch (sectionKey) {
    case 'segue':
      return <AIAssistedSegue currentMember={currentMember} />;

    case 'scorecard':
      const metrics = data?.scorecard?.metrics || [];
      const summary = data?.scorecard?.summary || {};
      
      // Filter metrics based on company level vs individual
      let displayMetrics = metrics;
      let scopeLabel = 'Company';
      let scopeDepartment = 'All Teams';
      
      if (!isCompanyLevel && currentMember) {
        // Get user's department and role
        const userDepartment = (currentMember.department || '').toLowerCase().trim();
        const userRole = (currentMember.role || '').toLowerCase().trim();
        
        // Find matching categories for this user
        let allowedCategories = DEPARTMENT_CATEGORY_MAP[userDepartment] || 
                               DEPARTMENT_CATEGORY_MAP[userRole] || 
                               [];
        
        // If still no match, try partial matching
        if (allowedCategories.length === 0) {
          for (const [key, cats] of Object.entries(DEPARTMENT_CATEGORY_MAP)) {
            if (userDepartment.includes(key) || key.includes(userDepartment) ||
                userRole.includes(key) || key.includes(userRole)) {
              allowedCategories = cats;
              break;
            }
          }
        }
        
        // Filter metrics by allowed categories
        if (allowedCategories.length > 0) {
          displayMetrics = metrics.filter(m => {
            const metricCategory = (m.category || '').toLowerCase();
            return allowedCategories.some(cat => 
              metricCategory.includes(cat) || cat.includes(metricCategory)
            );
          });
          
          // Set scope label based on categories
          if (allowedCategories.length === 1) {
            scopeDepartment = allowedCategories[0].charAt(0).toUpperCase() + allowedCategories[0].slice(1);
          } else if (allowedCategories.includes('platform') && allowedCategories.length >= 4) {
            scopeDepartment = 'All + Platform';
          } else if (allowedCategories.length >= 3) {
            scopeDepartment = 'Leadership View';
          } else {
            scopeDepartment = allowedCategories.map(c => c.charAt(0).toUpperCase()).join('/');
          }
        } else {
          // Default: show all metrics if no specific mapping found
          displayMetrics = metrics;
          scopeDepartment = 'All Metrics';
        }
        
        scopeLabel = currentMember.display_name?.split(' ')[0] || 'Individual';
      }
      
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold">
                {isCompanyLevel ? 'Company Scorecard' : `${scopeLabel}'s Metrics`}
              </h3>
              <Badge variant="outline" className={isCompanyLevel ? 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400' : 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-400'}>
                {isCompanyLevel ? <Building className="h-3 w-3 mr-1" /> : <User className="h-3 w-3 mr-1" />}
                {isCompanyLevel ? 'All Teams' : scopeDepartment}
              </Badge>
            </div>
            <div className="flex gap-2 text-xs">
              <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-400">
                {isCompanyLevel ? summary.green || 0 : displayMetrics.filter(m => m.weekly?.status === 'green').length} On Track
              </Badge>
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-400">
                {isCompanyLevel ? summary.yellow || 0 : displayMetrics.filter(m => m.weekly?.status === 'yellow').length} Warning
              </Badge>
              <Badge variant="outline" className="bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-400">
                {isCompanyLevel ? summary.red || 0 : displayMetrics.filter(m => m.weekly?.status === 'red').length} Off Track
              </Badge>
            </div>
          </div>
          
          {!isCompanyLevel && currentMember && (
            <p className="text-sm text-slate-400">
              Reviewing {scopeLabel}&apos;s key metrics and performance indicators.
            </p>
          )}
          
          <ScrollArea className="h-[280px]">
            {(() => {
              const metricsToShow = isCompanyLevel ? metrics : displayMetrics;
              if (metricsToShow.length === 0) {
                return (
                  <div className="text-center py-8 text-slate-500">
                    {isCompanyLevel 
                      ? 'No scorecard metrics configured' 
                      : `No specific metrics assigned to ${scopeLabel}`}
                  </div>
                );
              }
              
              // Group metrics by category
              const groupedMetrics = metricsToShow.reduce((acc, m) => {
                const cat = (m.category || 'other').toLowerCase();
                if (!acc[cat]) acc[cat] = [];
                acc[cat].push(m);
                return acc;
              }, {});
              
              // Category display names and order
              const categoryOrder = ['marketing', 'sales', 'operations', 'financial', 'platform', 'other'];
              const categoryLabels = {
                marketing: 'Marketing (Cometly)',
                sales: 'Sales',
                operations: 'Operations', 
                financial: 'Financial',
                platform: 'Platform',
                other: 'Other'
              };
              
              return (
                <div className="space-y-3">
                  {categoryOrder.map(cat => {
                    const catMetrics = groupedMetrics[cat];
                    if (!catMetrics || catMetrics.length === 0) return null;
                    
                    const isCollapsed = collapsedCategories[cat];
                    const greenCount = catMetrics.filter(m => m.weekly?.status === 'green').length;
                    const yellowCount = catMetrics.filter(m => m.weekly?.status === 'yellow').length;
                    const redCount = catMetrics.filter(m => m.weekly?.status === 'red').length;
                    
                    return (
                      <div key={cat} className="border border-slate-700 rounded-lg overflow-hidden">
                        {/* Category Header */}
                        <button
                          onClick={() => toggleCategory(cat)}
                          className="w-full flex items-center justify-between p-3 bg-slate-800/50 hover:bg-slate-800 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <ChevronRight className={`h-4 w-4 transition-transform ${!isCollapsed ? 'rotate-90' : ''}`} />
                            <span className="font-semibold text-sm">{categoryLabels[cat]}</span>
                            <span className="text-xs text-slate-500">({catMetrics.length})</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {greenCount > 0 && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">{greenCount}</span>
                            )}
                            {yellowCount > 0 && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">{yellowCount}</span>
                            )}
                            {redCount > 0 && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">{redCount}</span>
                            )}
                          </div>
                        </button>
                        
                        {/* Category Metrics */}
                        {!isCollapsed && (
                          <div className="divide-y divide-slate-700/50">
                            {catMetrics.map((m, i) => (
                              <div key={i} className={`p-3 flex justify-between items-center ${
                                m.weekly?.status === 'red' ? 'bg-red-500/5' : 
                                m.weekly?.status === 'yellow' ? 'bg-yellow-500/5' :
                                m.weekly?.status === 'green' ? 'bg-green-500/5' : ''
                              }`}>
                                <div className="flex-1">
                                  <div className="font-medium text-sm">{m.name}</div>
                                </div>
                                <div className="text-right flex items-center gap-3">
                                  <div>
                                    <div className="text-sm font-bold">{m.weekly?.actual ?? '—'}</div>
                                    {m.weekly?.goal && <div className="text-xs text-slate-500">Goal: {m.weekly?.goal}</div>}
                                  </div>
                                  <div className={`h-2 w-2 rounded-full ${
                                    m.weekly?.status === 'green' ? 'bg-green-500' :
                                    m.weekly?.status === 'yellow' ? 'bg-yellow-500' :
                                    m.weekly?.status === 'red' ? 'bg-red-500' : 'bg-slate-500'
                                  }`} />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </ScrollArea>
        </div>
      );

    case 'rocks':
      const rocks = data?.rocks || [];
      return (
        <div className="space-y-4">
          <h3 className="font-semibold">
            {currentMember ? `${currentMember.display_name?.split(' ')[0]}'s Rocks` : 'Rock Review'}
          </h3>
          <p className="text-sm text-slate-400">Are your rocks on track or off track?</p>
          <ScrollArea className="h-[280px]">
            {rocks.length > 0 ? (
              <div className="space-y-2">
                {rocks.map((r, i) => (
                  <div key={i} className="p-3 border rounded-lg border-purple-500/30 bg-purple-500/5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-purple-500" />
                        <span className="font-medium text-sm">{r.title}</span>
                      </div>
                      <Badge variant={r.status === 'on_track' ? 'default' : 'destructive'} className="text-xs">
                        {r.progress_percentage || 0}%
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                {currentMember ? `No rocks assigned to ${currentMember.display_name?.split(' ')[0]}` : 'No rocks to review'}
              </div>
            )}
          </ScrollArea>
        </div>
      );

    case 'headlines':
      const headlines = data?.headlines || [];
      return (
        <div className="space-y-4">
          <h3 className="font-semibold">
            {currentMember ? `${currentMember.display_name?.split(' ')[0]}'s Headlines` : 'Headlines'}
          </h3>
          <p className="text-sm text-slate-400">Customer/employee news, good or bad - just drop it, no discussion.</p>
          <ScrollArea className="h-[280px]">
            {headlines.length > 0 ? (
              <div className="space-y-2">
                {headlines.map((h, i) => (
                  <div key={i} className="p-3 border rounded-lg border-yellow-500/30 bg-yellow-500/5">
                    <p className="text-sm">{h.message}</p>
                    <p className="text-xs text-slate-500 mt-1">{h.category}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                {currentMember ? 'No headlines from this member' : 'No headlines this week'}
              </div>
            )}
          </ScrollArea>
        </div>
      );

    case 'todos':
      const todos = data?.todos || [];
      const completedTodos = todos.filter(t => t.status === 'complete').length;
      const totalTodos = todos.length;
      
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">
              {currentMember ? `${currentMember.display_name?.split(' ')[0]}'s To-Dos` : 'To-Do List'}
            </h3>
            <div className="flex gap-2">
              <Badge variant="outline" className="text-xs">
                {completedTodos}/{totalTodos} Complete
              </Badge>
            </div>
          </div>
          <p className="text-sm text-slate-400">Click checkbox to mark done or re-open</p>
          <ScrollArea className="h-[280px]">
            {todos.length > 0 ? (
              <div className="space-y-2">
                {todos.map((t) => (
                  <div 
                    key={t.id} 
                    className={`p-3 border rounded-lg flex items-center gap-3 transition-all ${
                      t.status === 'complete' 
                        ? 'border-green-500/30 bg-green-500/10' 
                        : 'border-cyan-500/30 bg-cyan-500/5'
                    } ${updatingTodoId === t.id ? 'opacity-50' : ''}`}
                  >
                    <Checkbox 
                      checked={t.status === 'complete'} 
                      disabled={updatingTodoId === t.id}
                      onCheckedChange={() => handleToggleTodoStatus(t.id, t.status)}
                      className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                    />
                    <div className="flex-1">
                      <span className={`text-sm ${t.status === 'complete' ? 'line-through text-slate-500' : ''}`}>
                        {t.task}
                      </span>
                    </div>
                    <Badge 
                      variant={t.status === 'complete' ? 'default' : t.status === 'overdue' ? 'destructive' : 'outline'} 
                      className={`text-xs ${t.status === 'complete' ? 'bg-green-600' : ''}`}
                    >
                      {updatingTodoId === t.id ? <Loader2 className="h-3 w-3 animate-spin" /> : (t.status === 'complete' ? 'Done ✓' : 'Open')}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                {currentMember ? 'No to-dos for this member' : 'No to-dos to review'}
              </div>
            )}
          </ScrollArea>
        </div>
      );

    case 'ids':
      const issues = data?.issues || [];
      
      // Filter issues by current member if applicable
      let displayIssues = issues;
      if (currentMember) {
        displayIssues = issues.filter(i => 
          i.owner_id === currentMember.user_id || 
          i.created_by === currentMember.user_id ||
          i.assigned_to === currentMember.user_id
        );
      }
      const openIssues = displayIssues.filter(i => i.status !== 'solved' && i.status !== 'archived');
      const solvedIssues = displayIssues.filter(i => i.status === 'solved');
      
      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">
              {currentMember 
                ? `${currentMember.display_name?.split(' ')[0]}'s Issues` 
                : 'IDS - Identify, Discuss, Solve'}
            </h3>
            <div className="flex gap-2">
              <Badge variant="outline" className="bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-400">
                {openIssues.length} Open
              </Badge>
              {solvedIssues.length > 0 && (
                <Badge variant="default" className="bg-green-600 text-white">
                  {solvedIssues.length} Solved ✓
                </Badge>
              )}
            </div>
          </div>
          <p className="text-sm text-slate-400">
            Click &quot;Discussing&quot; when starting an issue, &quot;Solved ✓&quot; when resolved
          </p>
          <ScrollArea className="h-[300px]">
            {openIssues.length > 0 || solvedIssues.length > 0 ? (
              <div className="space-y-2">
                {/* Open Issues */}
                {openIssues.map((issue) => (
                  <div 
                    key={issue.id} 
                    className={`p-3 border rounded-lg transition-all ${
                      issue.status === 'in_discussion' 
                        ? 'border-yellow-500 bg-yellow-500/10' 
                        : 'border-red-500/30 bg-red-500/5'
                    } ${updatingIssueId === issue.id ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Zap className={`h-4 w-4 flex-shrink-0 ${issue.priority === 'high' ? 'text-red-500' : 'text-yellow-500'}`} />
                      <span className="font-medium text-sm">{issue.title}</span>
                      <Badge variant={issue.priority === 'high' ? 'destructive' : 'outline'} className="text-xs">
                        {issue.priority}
                      </Badge>
                      {issue.status === 'in_discussion' && (
                        <Badge variant="outline" className="text-xs border-yellow-500 text-yellow-500">
                          <MessageSquare className="h-3 w-3 mr-1" />
                          Discussing
                        </Badge>
                      )}
                    </div>
                    {issue.description && (
                      <p className="text-xs text-slate-400 mb-2">{issue.description}</p>
                    )}
                    {/* IDS Action Buttons */}
                    <div className="flex gap-2">
                      {issue.status === 'identified' && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-7 text-xs border-yellow-500 text-yellow-500 hover:bg-yellow-500/20"
                          onClick={() => handleUpdateIssueStatus(issue.id, 'in_discussion')}
                          disabled={updatingIssueId === issue.id}
                        >
                          {updatingIssueId === issue.id ? (
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          ) : (
                            <MessageSquare className="h-3 w-3 mr-1" />
                          )}
                          Discussing
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="default" 
                        className="h-7 text-xs bg-green-600 hover:bg-green-700"
                        onClick={() => handleUpdateIssueStatus(issue.id, 'solved')}
                        disabled={updatingIssueId === issue.id}
                      >
                        {updatingIssueId === issue.id ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : (
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                        )}
                        Solved ✓
                      </Button>
                    </div>
                  </div>
                ))}
                
                {/* Recently Solved Issues */}
                {solvedIssues.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-slate-700">
                    <p className="text-xs text-green-500 font-medium mb-2">✓ Solved This Meeting</p>
                    {solvedIssues.slice(0, 5).map((issue) => (
                      <div 
                        key={issue.id} 
                        className="p-2 border rounded-lg border-green-500/30 bg-green-500/10 mb-1"
                      >
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          <span className="text-sm text-slate-400 line-through">{issue.title}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-green-500">
                <CheckCircle2 className="h-8 w-8 mx-auto mb-2" />
                {currentMember 
                  ? `No open issues for ${currentMember.display_name?.split(' ')[0]}!`
                  : 'No open issues! Great work!'}
              </div>
            )}
          </ScrollArea>
        </div>
      );

    case 'conclude':
      return (
        <MeetingConclude 
          meetingId={meetingId} 
          attendees={attendees} 
          sectionData={data}
        />
      );

    default:
      return <div className="text-center py-8 text-slate-500">Unknown section</div>;
  }
};

export default EOSL10MeetingRoomEnhanced;
